import * as dates from '@simplybenefits/sb-dates';

const DAY_IN_SECONDS = 24 * 60 * 60;

/**
 * Calculates remaining days in quarter
 */
function getDaysLeftInQuarter(year, lastMonthOfQuarter, adjustedDate) {
    const adjustedDateUnix = Math.floor(adjustedDate.getTime() / 1000);
    const lastDayOfQuarter = new Date(Date.UTC(year, lastMonthOfQuarter, 0));
    const endOfDayForQuarter = dates.getEndOfDay(lastDayOfQuarter);
    return Math.ceil((endOfDayForQuarter - adjustedDateUnix) / DAY_IN_SECONDS);
}

/**
 * Calculates remaining days in year
 */
function getDaysLeftInYear(date, year) {
    const endOfYear = dates.getEndOfDay(`${ year }-12-31`);
    const dateInUnix = Math.floor(date.getTime() / 1000);
    const startOfDay = dates.getStartOfDay(dateInUnix);
    const timeLeftInYear = endOfYear - startOfDay;
    return Math.ceil(timeLeftInYear / DAY_IN_SECONDS);
}

/**
 * Checks if year is a leap year and returns days
 */
function getDaysInYear(year) {
    // https://stackoverflow.com/questions/16353211/check-if-year-is-leap-year-in-javascript not a bad idea to put the link in
    const isLeapYear = (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
    return isLeapYear ? 366 : 365;
}

/**
 * Gets quarter number from date
 */
function getQuarter(date) {
    return Math.floor((date.getUTCMonth() + 3) / 3);
}

/**
 * Adjusts eligibility date to current year if needed
 */
function adjustEligibilityForYear(eligibilityDate) {
    const eligibilityYear = new Date(eligibilityDate * 1000).getUTCFullYear();
    const currentYear = new Date().getFullYear();
    let adjustedDate = eligibilityDate;

    if (eligibilityYear < currentYear) {
        adjustedDate = dates.getStartOfDay(`${ currentYear }-01-01`);
    }

    return new Date(adjustedDate * 1000);
}

/**
 * Calculates quarterly allowance balances
 */
function calculateQuarterlyAllowance(eligibilityDate, hsaMax, lsaMax, isProrated = false) {
    const date = adjustEligibilityForYear(eligibilityDate);
    const eligibleQuarter = getQuarter(date);
    let quarterlyFactor = Math.max(0, 4 - eligibleQuarter); // last quarter of the year minus eligible quarter

    // add 1 so quarter eligible is given for non prorated balances
    if (!isProrated) {
        quarterlyFactor += 1;
    }

    return {
        proratedHsa: (hsaMax / 4) * quarterlyFactor,
        proratedLsa: (lsaMax / 4) * quarterlyFactor
    };
}

/**
 * Calculates prorated amounts
 */
function calculateProratedAmount(eligibilityDate, proratedBy, hsaMax, lsaMax, isQuarterlyAllowance = false) {
    const adjustedDate = adjustEligibilityForYear(eligibilityDate);
    const month = adjustedDate.getUTCMonth() + 1;
    const year = adjustedDate.getUTCFullYear();
    const isFirstDayOfMonth = adjustedDate.getUTCDate() === 1;

    // Quarter eligibility calculations
    const isFirstMonthOfQuarter = (month - 1) % 3 === 0;
    const eligibleQuarter = getQuarter(adjustedDate);
    const lastMonthOfQuarter = eligibleQuarter * 3;

    let remaining = null;
    let divisor = null;
    switch (proratedBy) {
        case 'day':
            remaining = isQuarterlyAllowance
                ? getDaysLeftInQuarter(year, lastMonthOfQuarter, adjustedDate)
                : getDaysLeftInYear(adjustedDate, year);

            divisor = getDaysInYear(year);

            break;

        case 'month':
            remaining = isQuarterlyAllowance
                ? lastMonthOfQuarter - month
                : 12 - month;

            if (isFirstDayOfMonth) {
                remaining += 1;
            }

            divisor = 12;
            break;

        case 'quarter':
            remaining = 4 - eligibleQuarter;

            if (isFirstMonthOfQuarter && isFirstDayOfMonth) {
                remaining += 1;
            }

            divisor = 4;
            break;

        default:
            return {
                proratedHsa: hsaMax,
                proratedLsa: lsaMax
            };
    }

    return {
        proratedHsa: (hsaMax / divisor) * remaining,
        proratedLsa: (lsaMax / divisor) * remaining
    };
}

/**
 * Wrapper function to calculate final balances based on settings (called during user signup)
 */
function calculateBalances(eligibilityDate, proratedBy, hsaMax, lsaMax, isQuarterlyAllowance = false, isProrated = false) {
    if (!isQuarterlyAllowance && !isProrated) {
        return {
            proratedHsa: hsaMax,
            proratedLsa: lsaMax
        };
    }

    if (isQuarterlyAllowance && !isProrated) {
        const { proratedHsa, proratedLsa } = calculateQuarterlyAllowance(eligibilityDate, hsaMax, lsaMax);

        return {
            proratedHsa: Math.ceil(proratedHsa),
            proratedLsa: Math.ceil(proratedLsa)
        };
    }

    if (isProrated && !isQuarterlyAllowance) {
        const { proratedHsa, proratedLsa } = calculateProratedAmount(eligibilityDate, proratedBy, hsaMax, lsaMax);

        return {
            proratedHsa: Math.ceil(proratedHsa),
            proratedLsa: Math.ceil(proratedLsa)
        };
    }

    // Both proration and quarterly allowance are enabled
    const proratedAmounts = calculateProratedAmount(eligibilityDate, proratedBy, hsaMax, lsaMax, true);
    const quarterlyAmounts = calculateQuarterlyAllowance(eligibilityDate, hsaMax, lsaMax, true);

    return {
        proratedHsa: Math.ceil(proratedAmounts.proratedHsa + quarterlyAmounts.proratedHsa),
        proratedLsa: Math.ceil(proratedAmounts.proratedLsa + quarterlyAmounts.proratedLsa)
    };
}

/**
 * Calculates manually triggered balance reset amounts with both proration and quarterly allowance
 */
function calculateProratedBalances(eligibilityDate, proratedBy, hsaMax, lsaMax, isQuarterlyAllowance = false) {
    if (!isQuarterlyAllowance) {
        const { proratedHsa, proratedLsa } = calculateProratedAmount(eligibilityDate, proratedBy, hsaMax, lsaMax, false);

        return {
            proratedHsa: Math.ceil(proratedHsa),
            proratedLsa: Math.ceil(proratedLsa)
        };
    }

    const proratedAmounts = calculateProratedAmount(eligibilityDate, proratedBy, hsaMax, lsaMax, true);
    const quarterlyAmounts = calculateQuarterlyAllowance(eligibilityDate, hsaMax, lsaMax, true);

    return {
        proratedHsa: Math.ceil(proratedAmounts.proratedHsa + quarterlyAmounts.proratedHsa),
        proratedLsa: Math.ceil(proratedAmounts.proratedLsa + quarterlyAmounts.proratedLsa)
    };
}

export { calculateBalances, calculateProratedBalances };